import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  dailyLogsApi, equipmentApi, projectsApi, qaqcApi, usersApi,
  type DailyLog, type DailyLogCrew, type DailyLogWork, type DailyLogQuantity,
} from '@/lib/apiClient';
import { StateView, Badge, Button } from '@/components';
import type { BadgeTone, StateViewQuery } from '@/components';
import { fmtDate } from '@/lib/dates';
import SitePhotoThread from './SitePhotoThread';
import { statusBadge, weekday, weatherSummary, num, DAILY_LOGS_ROLLUP_KEY } from './dailyLogFormat';

/**
 * Who can write a daily log.
 *
 * MIRRORS THE API EXACTLY: daily-logs.controller.ts and attachments.controller.ts
 * both accept `projects.edit` OR `daily_logs.edit`, so this must be an OR too.
 * Getting it wrong in either direction is a live bug — `daily_logs.edit` is the
 * ONLY permission the FieldLeader role holds (16 users on prod), so checking just
 * `projects.edit` would lock the crew out of the one thing they're here to do.
 *
 * Before this existed the tab had NO permission check at all: the Daily Logs tab
 * is shown to everyone, so Start Today's Log / Save draft / Submit log rendered
 * for all 63 prod users and 17 of them — Executive/Accounting, ReadOnly,
 * EquipmentManager, PprCoordinator, and 6 accounts with no role — filled in a
 * whole log before eating a 403 on save.
 *
 * Follows the useCanEditSurvey() shape in modules/survey/SurveyPage.tsx.
 *
 * Also the Photos tab's "may post" hint (#665): posting photos follows the
 * daily-log write rule, whichever way into the feed the reader came.
 */
export function useCanEditDailyLogs(): boolean {
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => usersApi.me(),
    staleTime: 5 * 60 * 1000,
  });
  return (me?.roles.includes('Admin') ?? false)
    || (me?.permissions.includes('projects.edit') ?? false)
    || (me?.permissions.includes('daily_logs.edit') ?? false);
}

// Equipment status → Badge tone (loose, case-insensitive match on the label).
function equipTone(label: string): BadgeTone {
  const s = (label || '').toLowerCase();
  if (s.includes('rent') || s.includes('use') || s.includes('site')) return 'success';
  if (s.includes('request') || s.includes('order') || s.includes('approv')) return 'info';
  return 'neutral';
}

// statusBadge / weekday / weatherSummary / num live in ./dailyLogFormat — shared
// with the company-wide Daily Logs rollup so the two cannot drift.

const css = `
  .dl-table { width: 100%; border-collapse: collapse; font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .dl-table th {
    text-align: left; padding: 10px var(--ew-s-4); white-space: nowrap;
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500);
    border-bottom: 1px solid var(--ew-n-100);
  }
  .dl-table td { padding: 11px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); color: var(--ew-dark); vertical-align: top; }
  .dl-table tr:last-child td { border-bottom: 0; }
  .dl-row { cursor: pointer; transition: background var(--ew-dur-fast) var(--ew-ease); }
  .dl-row:hover { background: var(--ew-n-50); }
  .dl-muted { color: var(--ew-n-500); }
  .dl-num { font-variant-numeric: tabular-nums; white-space: nowrap; }

  /* Start Today's Log CTA */
  .dl-cta {
    display: flex; align-items: center; justify-content: space-between; gap: var(--ew-s-4);
    flex-wrap: wrap; padding: var(--ew-s-5);
    background: var(--ew-green-50); border-color: var(--ew-green);
  }
  .dl-cta-title { font: var(--ew-fw-semibold) var(--ew-fs-h4)/1.2 var(--ew-font); color: var(--ew-green-700); margin: 0; }
  .dl-cta-sub { font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-n-600); margin: 4px 0 0; }

  /* Composer / viewer */
  .dl-back {
    background: transparent; border: 0; cursor: pointer; padding: 0;
    color: var(--ew-n-500); font: var(--ew-fw-medium) var(--ew-fs-body-sm) var(--ew-font);
    transition: color var(--ew-dur-fast) var(--ew-ease);
  }
  .dl-back:hover { color: var(--ew-dark); }
  .dl-section { padding: var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); }
  .dl-section:last-child { border-bottom: 0; }
  .dl-field { display: flex; flex-direction: column; }
  .dl-label { font: var(--ew-fw-medium) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500); margin-bottom: 5px; }
  .dl-input {
    border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 7px 10px;
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); width: 100%;
    background: #fff; color: var(--ew-dark);
  }
  .dl-input:disabled { background: var(--ew-n-50); color: var(--ew-n-600); }
  .dl-weather { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: var(--ew-s-3); }
  .dl-weather .dl-field:first-child { grid-column: 1 / -1; }
  /* The four temp/precip/wind inputs are unusably narrow at 4-up on a phone. */
  @media (max-width: 600px) { .dl-weather { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
  .dl-prose { font: var(--ew-fw-regular) var(--ew-fs-body) var(--ew-font); color: var(--ew-dark); line-height: 1.5; white-space: pre-wrap; }
  .dl-grid-row { display: grid; gap: var(--ew-s-2); align-items: center; }
  .dl-grid-head { font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500); }
  .dl-addbtn {
    background: transparent; border: 1px dashed var(--ew-n-300); border-radius: var(--ew-r-md);
    padding: 7px 12px; cursor: pointer; color: var(--ew-n-600);
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1 var(--ew-font); width: 100%;
    transition: all var(--ew-dur-fast) var(--ew-ease);
  }
  .dl-addbtn:hover { border-color: var(--ew-green); color: var(--ew-green-700); }
  .dl-removebtn {
    background: transparent; border: 0; cursor: pointer; color: var(--ew-n-400); padding: 4px 8px;
    font-size: 18px; line-height: 1; transition: color var(--ew-dur-fast) var(--ew-ease);
  }
  .dl-removebtn:hover { color: var(--ew-danger); }
  /* Daily logs | Photos switch */
  .dl-views {
    display: inline-flex; gap: 2px; padding: 3px; margin-bottom: var(--ew-s-4);
    background: var(--ew-n-100); border-radius: var(--ew-r-lg);
  }
  .dl-view {
    border: 0; background: transparent; cursor: pointer; border-radius: var(--ew-r-md);
    padding: 8px 16px; min-height: 36px; color: var(--ew-n-600);
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1 var(--ew-font);
  }
  .dl-view[aria-pressed="true"] { background: #fff; color: var(--ew-dark); box-shadow: var(--ew-shadow-1); }
  @media (max-width: 600px) { .dl-views { display: flex; } .dl-view { flex: 1; } }
  .dl-banner-err { margin-bottom: var(--ew-s-3); padding: 8px 12px; background: #FBE4DF; color: #8F2414; border-radius: var(--ew-r-md); font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
`;

export default function DailyLogsTab({ projectId, locked = false }: { projectId: number; locked?: boolean }) {
  const canEdit = useCanEditDailyLogs();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('log');
  // ?view=photos → the site photo thread; anything else → the logs list.
  // The same thread is the job's Photos tab (#665). This switch stays because
  // Daily Logs is where field leaders work on their phones, and existing
  // ?view=photos links land here; both open the one feed, so neither can
  // drift from the other.
  const view = params.get('view') === 'photos' ? 'photos' : 'logs';

  const query = useQuery({
    queryKey: ['daily-logs', projectId],
    queryFn: () => dailyLogsApi.list({ projectId }),
  });

  // ?log=<id> → composer/viewer; keep ?tab=daily intact when toggling.
  const openLog = (id: number) => {
    const next = new URLSearchParams(params);
    next.set('tab', 'daily');
    next.set('log', String(id));
    setParams(next, { replace: true });
  };
  const backToList = () => {
    const next = new URLSearchParams(params);
    next.delete('log');
    setParams(next, { replace: true });
  };
  const setView = (v: 'logs' | 'photos') => {
    const next = new URLSearchParams(params);
    next.set('tab', 'daily');
    if (v === 'photos') next.set('view', 'photos'); else next.delete('view');
    setParams(next, { replace: true });
  };

  if (selectedId) {
    return (
      <>
        <style>{css}</style>
        <DailyLogDetail projectId={projectId} logId={Number(selectedId)} onBack={backToList} canEdit={canEdit} locked={locked} />
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      <div className="dl-views" role="group" aria-label="Show">
        <button type="button" className="dl-view" aria-pressed={view === 'logs'} onClick={() => setView('logs')}>Daily logs</button>
        <button type="button" className="dl-view" aria-pressed={view === 'photos'} onClick={() => setView('photos')}>Photos</button>
      </div>
      {view === 'photos' ? (
        <SitePhotoThread projectId={projectId} canPost={canEdit} locked={locked} onOpenLog={openLog} />
      ) : (
        <DailyLogHistory projectId={projectId} query={query} onOpen={openLog} locked={locked} canEdit={canEdit} />
      )}
    </>
  );
}

// ─── History view (Start Today's Log CTA + history table) ───────────────────
function DailyLogHistory({
  projectId,
  query,
  onOpen,
  locked = false,
  canEdit,
}: {
  projectId: number;
  query: StateViewQuery<DailyLog[]>;
  onOpen: (id: number) => void;
  locked?: boolean;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const startMutation = useMutation({
    mutationFn: () => dailyLogsApi.startToday({ projectId }),
    onSuccess: (log) => {
      // A new draft belongs on the company-wide rollup straight away.
      qc.invalidateQueries({ queryKey: DAILY_LOGS_ROLLUP_KEY });
      onOpen(log.id);
    },
  });

  return (
    <>
      {/* Start CTA hidden while activity-locked (SYSTAC / ClosedWon) — the
          workspace banner explains why; the API blocks the create regardless.
          Also hidden without write access: POST /daily-logs requires
          projects.edit OR daily_logs.edit, so for anyone else this button only
          ever produced a 403. */}
      {locked || !canEdit ? null : (
      <div className="pw-card dl-cta" style={{ marginBottom: 'var(--ew-s-4)' }}>
        <div>
          <h2 className="dl-cta-title">Start Today's Log</h2>
          <p className="dl-cta-sub">Open today's daily report, or resume the existing draft for today.</p>
          {startMutation.isError && (
            <p className="dl-cta-sub" style={{ color: 'var(--ew-danger)' }}>
              {(startMutation.error as Error).message}
            </p>
          )}
        </div>
        <Button
          variant="primary"
          onClick={() => startMutation.mutate()}
          isLoading={startMutation.isPending}
        >
          Start Today's Log
        </Button>
      </div>
      )}

      <div className="pw-card">
        <h2 className="pw-card-hd">Daily Logs</h2>
        <StateView query={query} empty="No daily logs yet.">
          {(logs) => (
            <div className="ew-table-scroll">
              <table className="dl-table ew-cards">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Author</th>
                    <th>Weather</th>
                    <th>Workers</th>
                    <th>Man-hours</th>
                    <th>Summary</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const b = statusBadge(log.status);
                    return (
                      <tr key={log.id} className="dl-row" onClick={() => onOpen(log.id)}>
                        <td className="dl-num" data-label="Date" style={{ whiteSpace: 'nowrap' }}>
                          {fmtDate(log.date)}
                          <span className="dl-muted"> · {weekday(log.date)}</span>
                        </td>
                        <td className="dl-muted" data-label="Author">{log.author ?? '—'}</td>
                        <td className="dl-muted" data-label="Weather">{weatherSummary(log)}</td>
                        <td className="dl-num" data-label="Workers">{num(log.totalWorkers)}</td>
                        <td className="dl-num" data-label="Man-hours">{num(log.totalManhours)}</td>
                        <td className="dl-muted" data-label="Summary" style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.workSummary?.trim() || '—'}
                        </td>
                        <td data-label="Status"><Badge label={b.label} tone={b.tone} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </StateView>
      </div>
    </>
  );
}

// ─── Composer / viewer ───────────────────────────────────────────────────────
// Local editable state for the four header weather fields, work summary, delays,
// safety notes, plus the three child collections. Hydrated from the fetched log.
type Crew = { company: string; trade: string; workers: string; hours: string };
type Work = { area: string; narrative: string };
type Qty = { item: string; qty: string; unit: string };

function DailyLogDetail({
  projectId,
  logId,
  onBack,
  canEdit,
  locked = false,
}: {
  projectId: number;
  logId: number;
  onBack: () => void;
  canEdit: boolean;
  locked?: boolean;
}) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['daily-log', logId],
    queryFn: () => dailyLogsApi.get(logId),
  });

  // Equipment on site (read-only mirror from the Equipment module).
  const equipQuery = useQuery<any[]>({
    queryKey: ['equipment', projectId],
    queryFn: () => equipmentApi.list({ projectId }) as Promise<any[]>,
    staleTime: 60_000,
    retry: false,
  });

  // ── Editable local state ──
  const [weatherCond, setWeatherCond] = useState('');
  const [weatherHi, setWeatherHi] = useState('');
  const [weatherLo, setWeatherLo] = useState('');
  const [weatherPrecip, setWeatherPrecip] = useState('');
  const [weatherWind, setWeatherWind] = useState('');
  const [workSummary, setWorkSummary] = useState('');
  const [delays, setDelays] = useState('');
  const [safetyNotes, setSafetyNotes] = useState('');
  const [crews, setCrews] = useState<Crew[]>([]);
  const [work, setWork] = useState<Work[]>([]);
  const [quantities, setQuantities] = useState<Qty[]>([]);
  // Confirmed QA/QC pour cards to mark as placed with this log (flip on save
  // or submit). pourActuals holds the "actual CY poured" per checked card —
  // yield tracking against the pre-pour estimate.
  const [pouredIds, setPouredIds] = useState<number[]>([]);
  const [pourActuals, setPourActuals] = useState<Record<number, string>>({});
  const poursQuery = useQuery({
    queryKey: ['qaqc-pours', projectId],
    queryFn: () => qaqcApi.listPourCards(projectId),
    staleTime: 60_000,
    retry: false,
  });
  // Concrete only: a rock delivery has no "actual CY poured" to record here,
  // and is marked delivered from the QA/QC tab instead.
  const confirmedPours = (poursQuery.data ?? []).filter((pc) => pc.status === 'confirmed' && pc.pourType !== 'rock');
  // Pours this log already flipped (shown read-only after submit). Concrete
  // only here too: a card flipped as concrete and later corrected to rock
  // keeps its daily-log stamp (the record of how it was marked), but isn't a
  // concrete pour any more.
  const flippedPours = (poursQuery.data ?? []).filter((pc) => pc.pouredSource === 'daily-log' && pc.dailyLogId === logId && pc.pourType !== 'rock');

  // Weather auto-fill (free NWS, same-day) — populates the editable fields below.
  const [wxLoading, setWxLoading] = useState(false);
  const [wxNote, setWxNote] = useState<string | null>(null);
  const autofillWeather = async () => {
    setWxLoading(true);
    setWxNote(null);
    try {
      const w = await projectsApi.weather(projectId);
      if (!w.available) {
        setWxNote(w.reason === 'no_address'
          ? 'No site address on file for this job — add one in Overview to auto-fill.'
          : "Couldn't fetch weather for the site right now.");
        return;
      }
      if (w.cond != null) setWeatherCond(w.cond);
      if (w.hi != null) setWeatherHi(String(w.hi));
      if (w.lo != null) setWeatherLo(String(w.lo));
      if (w.precip != null) setWeatherPrecip(w.precip);
      if (w.windSpeed != null) setWeatherWind(w.windSpeed);
      setWxNote('Filled from the NWS forecast — review, then Save.');
    } catch (e) {
      setWxNote(e instanceof Error ? e.message : 'Weather lookup failed.');
    } finally {
      setWxLoading(false);
    }
  };

  // Hydrate local state ONCE per log — never on refetches. The query refetches
  // in the background (window focus, invalidations), and re-hydrating from
  // every refetch silently replaced whatever the user was typing with the
  // server's old copy — the intermittent "notes didn't save / saved then
  // disappeared" report. After our own Save, local state already equals what
  // was written, so skipping re-hydration is also correct there.
  const log = query.data;
  const hydratedLogId = useRef<number | null>(null);
  useEffect(() => {
    if (!log) return;
    if (hydratedLogId.current === logId) return;
    hydratedLogId.current = logId;
    setWeatherCond(log.weatherCond ?? '');
    setWeatherHi(log.weatherHi != null ? String(log.weatherHi) : '');
    setWeatherLo(log.weatherLo != null ? String(log.weatherLo) : '');
    setWeatherPrecip(log.weatherPrecip ?? '');
    setWeatherWind(log.weatherWind ?? '');
    setWorkSummary(log.workSummary ?? '');
    setDelays(log.delays ?? '');
    setSafetyNotes(log.safetyNotes ?? '');
    setCrews(
      (log.crews ?? []).map((c: DailyLogCrew) => ({
        company: c.company ?? '',
        trade: c.trade ?? '',
        workers: c.workers != null ? String(c.workers) : '',
        hours: c.hours != null ? String(c.hours) : '',
      })),
    );
    setWork(
      (log.work ?? []).map((w: DailyLogWork) => ({
        area: w.area ?? '',
        narrative: w.narrative ?? '',
      })),
    );
    setQuantities(
      (log.quantities ?? []).map((q: DailyLogQuantity) => ({
        item: q.item ?? '',
        qty: q.qty != null ? String(q.qty) : '',
        unit: q.unit ?? '',
      })),
    );
  }, [log, logId]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['daily-logs', projectId] });
    qc.invalidateQueries({ queryKey: ['daily-log', logId] });
    // The company-wide rollup lists this log too, with its status.
    qc.invalidateQueries({ queryKey: DAILY_LOGS_ROLLUP_KEY });
  };

  const numOrUndef = (v: string) => (v.trim() === '' ? undefined : Number(v));
  const strOrUndef = (v: string) => (v.trim() === '' ? undefined : v.trim());

  const buildPayload = () => ({
    weatherCond: strOrUndef(weatherCond),
    weatherHi: numOrUndef(weatherHi),
    weatherLo: numOrUndef(weatherLo),
    weatherPrecip: strOrUndef(weatherPrecip),
    weatherWind: strOrUndef(weatherWind),
    workSummary: strOrUndef(workSummary),
    delays: strOrUndef(delays),
    safetyNotes: strOrUndef(safetyNotes),
    crews: crews.map((c) => ({
      company: strOrUndef(c.company),
      trade: strOrUndef(c.trade),
      workers: numOrUndef(c.workers),
      hours: numOrUndef(c.hours),
    })),
    work: work.map((w) => ({
      area: strOrUndef(w.area),
      narrative: strOrUndef(w.narrative),
    })),
    quantities: quantities.map((q) => ({
      item: strOrUndef(q.item),
      qty: numOrUndef(q.qty),
      unit: strOrUndef(q.unit),
    })),
    pouredPourCards: pouredIds.length
      ? pouredIds.map((id) => ({ id, actualQtyCy: numOrUndef(pourActuals[id] ?? '') }))
      : undefined,
  });

  const afterSave = () => {
    invalidate();
    if (pouredIds.length) {
      setPouredIds([]);
      setPourActuals({});
      qc.invalidateQueries({ queryKey: ['qaqc-pours', projectId] });
      qc.invalidateQueries({ queryKey: ['project-counts', projectId] });
    }
  };

  const saveMutation = useMutation({
    mutationFn: () => dailyLogsApi.update(logId, buildPayload()),
    onSuccess: afterSave,
  });

  // Submit SAVES FIRST, then locks. The old "save your changes first" hint
  // was a trap — checking a pour (or typing anything) and hitting Submit
  // silently dropped the edits.
  const submitMutation = useMutation({
    mutationFn: async () => {
      await dailyLogsApi.update(logId, buildPayload());
      return dailyLogsApi.submit(logId);
    },
    onSuccess: afterSave,
  });

  return (
    <>
      <button className="dl-back" onClick={onBack} style={{ marginBottom: 'var(--ew-s-4)' }}>← Back to Daily Logs</button>

      <StateView query={query} isEmpty={(d) => !d} empty="Daily log not found.">
        {(d) => {
          // Submitted logs are locked for everyone; a viewer without write access
          // gets the same read-only treatment. Folding both into one flag means
          // every field, the Actions card and the photo uploader all follow it —
          // there's no second place to forget.
          const readOnly = d.status === 'submitted' || !canEdit;
          const b = statusBadge(d.status);
          const totalManhours = crews.reduce(
            (sum, c) => sum + (Number(c.workers) || 0) * (Number(c.hours) || 0),
            0,
          );

          return (
            <div className="ew-two-col">
              {/* Left column — composer */}
              <div style={{ display: 'grid', gap: 'var(--ew-s-4)' }}>
                {/* Header */}
                <div className="pw-card">
                  <div className="dl-section" style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-2)', flexWrap: 'wrap' }}>
                    <span style={{ font: 'var(--ew-fw-semibold) 20px/1 var(--ew-font)' }}>{fmtDate(d.date)}</span>
                    <span className="dl-muted">{weekday(d.date)}</span>
                    <Badge label={b.label} tone={b.tone} />
                    {readOnly && d.author ? (
                      <span className="dl-muted" style={{ marginLeft: 'auto', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>
                        Submitted by {d.author}
                      </span>
                    ) : null}
                  </div>

                  {/* Weather */}
                  <div className="dl-section">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-2)', marginBottom: 'var(--ew-s-1)' }}>
                      <p className="dl-label" style={{ margin: 0 }}>Weather</p>
                      {!readOnly ? (
                        <button type="button" className="dl-addbtn" style={{ marginLeft: 'auto', padding: '4px 10px' }} onClick={autofillWeather} disabled={wxLoading}>
                          {wxLoading ? 'Fetching…' : '⛅ Auto-fill from site'}
                        </button>
                      ) : null}
                    </div>
                    {wxNote ? <p className="dl-muted" style={{ margin: '0 0 var(--ew-s-2)', font: 'var(--ew-fw-regular) 12px var(--ew-font)' }}>{wxNote}</p> : null}
                    <div className="dl-weather">
                      <div className="dl-field">
                        <label className="dl-label">Conditions</label>
                        <input className="dl-input" value={weatherCond} onChange={(e) => setWeatherCond(e.target.value)} disabled={readOnly} placeholder="e.g. Sunny" />
                      </div>
                      <div className="dl-field">
                        <label className="dl-label">High °</label>
                        <input className="dl-input" type="number" value={weatherHi} onChange={(e) => setWeatherHi(e.target.value)} disabled={readOnly} />
                      </div>
                      <div className="dl-field">
                        <label className="dl-label">Low °</label>
                        <input className="dl-input" type="number" value={weatherLo} onChange={(e) => setWeatherLo(e.target.value)} disabled={readOnly} />
                      </div>
                      <div className="dl-field">
                        <label className="dl-label">Precip</label>
                        <input className="dl-input" value={weatherPrecip} onChange={(e) => setWeatherPrecip(e.target.value)} disabled={readOnly} placeholder="e.g. 0.2 in" />
                      </div>
                      <div className="dl-field">
                        <label className="dl-label">Wind</label>
                        <input className="dl-input" value={weatherWind} onChange={(e) => setWeatherWind(e.target.value)} disabled={readOnly} placeholder="e.g. 10 mph" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Crews */}
                <div className="pw-card">
                  <h2 className="pw-card-hd">Crews on site
                    <span style={{ marginLeft: 'auto', font: 'var(--ew-fw-regular) 12px var(--ew-font)', color: 'var(--ew-n-500)', textTransform: 'none', letterSpacing: 0 }}>
                      {num(totalManhours)} man-hours
                    </span>
                  </h2>
                  <div className="dl-section">
                    {crews.length > 0 ? (
                      <div style={{ display: 'grid', gap: 'var(--ew-s-2)', marginBottom: 'var(--ew-s-3)' }}>
                        <div className="dl-grid-row dl-grid-head" style={{ gridTemplateColumns: '2fr 1.5fr 0.8fr 0.8fr 0.8fr 28px' }}>
                          <span>Company</span><span>Trade</span><span>Workers</span><span>Hours</span><span>Man-hrs</span><span />
                        </div>
                        {crews.map((c, i) => {
                          const mh = (Number(c.workers) || 0) * (Number(c.hours) || 0);
                          return (
                            <div key={i} className="dl-grid-row" style={{ gridTemplateColumns: '2fr 1.5fr 0.8fr 0.8fr 0.8fr 28px' }}>
                              <input className="dl-input" value={c.company} onChange={(e) => setCrews((p) => p.map((x, j) => j === i ? { ...x, company: e.target.value } : x))} disabled={readOnly} placeholder="Company" />
                              <input className="dl-input" value={c.trade} onChange={(e) => setCrews((p) => p.map((x, j) => j === i ? { ...x, trade: e.target.value } : x))} disabled={readOnly} placeholder="Trade" />
                              <input className="dl-input" type="number" value={c.workers} onChange={(e) => setCrews((p) => p.map((x, j) => j === i ? { ...x, workers: e.target.value } : x))} disabled={readOnly} />
                              <input className="dl-input" type="number" value={c.hours} onChange={(e) => setCrews((p) => p.map((x, j) => j === i ? { ...x, hours: e.target.value } : x))} disabled={readOnly} />
                              <span className="dl-num" style={{ alignSelf: 'center', color: 'var(--ew-n-600)' }}>{num(mh)}</span>
                              {!readOnly ? (
                                <button className="dl-removebtn" onClick={() => setCrews((p) => p.filter((_, j) => j !== i))} aria-label="Remove crew">×</button>
                              ) : <span />}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="dl-muted" style={{ font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)', margin: '0 0 var(--ew-s-3)' }}>No crews recorded.</p>
                    )}
                    {!readOnly ? (
                      <button className="dl-addbtn" onClick={() => setCrews((p) => [...p, { company: '', trade: '', workers: '', hours: '' }])}>+ Add crew</button>
                    ) : null}
                  </div>
                </div>

                {/* Work performed */}
                <div className="pw-card">
                  <h2 className="pw-card-hd">Work performed</h2>
                  <div className="dl-section">
                    {work.length > 0 ? (
                      <div style={{ display: 'grid', gap: 'var(--ew-s-3)', marginBottom: 'var(--ew-s-3)' }}>
                        {work.map((w, i) => (
                          <div key={i} style={{ display: 'grid', gap: 'var(--ew-s-2)' }}>
                            <div style={{ display: 'flex', gap: 'var(--ew-s-2)', alignItems: 'center' }}>
                              <input className="dl-input" value={w.area} onChange={(e) => setWork((p) => p.map((x, j) => j === i ? { ...x, area: e.target.value } : x))} disabled={readOnly} placeholder="Area / location" />
                              {!readOnly ? (
                                <button className="dl-removebtn" onClick={() => setWork((p) => p.filter((_, j) => j !== i))} aria-label="Remove area">×</button>
                              ) : null}
                            </div>
                            <textarea className="dl-input" style={{ minHeight: 64, resize: 'vertical' }} value={w.narrative} onChange={(e) => setWork((p) => p.map((x, j) => j === i ? { ...x, narrative: e.target.value } : x))} disabled={readOnly} placeholder="What was done…" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="dl-muted" style={{ font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)', margin: '0 0 var(--ew-s-3)' }}>No work recorded.</p>
                    )}
                    {!readOnly ? (
                      <button className="dl-addbtn" onClick={() => setWork((p) => [...p, { area: '', narrative: '' }])}>+ Add area</button>
                    ) : null}
                  </div>
                </div>

                {/* Photos for this site day — the same thread as the Photos
                    tab, narrowed to the log's date. Deliberately NOT tied to
                    readOnly: photos can still be added after a log is submitted,
                    and each one records who posted it and when. */}
                {/* The LOG's job, not the page's: ?log= can name any log the
                    user can see, and photos added here must go to that log's
                    job and Job Folder (review of #560). The API still checks
                    access and the activity lock against this job. The page's
                    `locked` describes the PAGE's job, so it only applies when
                    that is the log's job; otherwise the list's canPost, which
                    carries the log's job's own lock, decides alone. */}
                <SitePhotoThread
                  projectId={d.projectId ?? projectId}
                  canPost={canEdit}
                  locked={(d.projectId ?? projectId) === projectId ? locked : false}
                  fixedDate={String(d.date).slice(0, 10)}
                />

                {/* Quantities installed */}
                <div className="pw-card">
                  <h2 className="pw-card-hd">Quantities installed</h2>
                  <div className="dl-section">
                    {quantities.length > 0 ? (
                      <div style={{ display: 'grid', gap: 'var(--ew-s-2)', marginBottom: 'var(--ew-s-3)' }}>
                        <div className="dl-grid-row dl-grid-head" style={{ gridTemplateColumns: '2fr 1fr 1fr 28px' }}>
                          <span>Item</span><span>Qty</span><span>Unit</span><span />
                        </div>
                        {quantities.map((q, i) => (
                          <div key={i} className="dl-grid-row" style={{ gridTemplateColumns: '2fr 1fr 1fr 28px' }}>
                            <input className="dl-input" value={q.item} onChange={(e) => setQuantities((p) => p.map((x, j) => j === i ? { ...x, item: e.target.value } : x))} disabled={readOnly} placeholder="e.g. Rebar" />
                            <input className="dl-input" type="number" value={q.qty} onChange={(e) => setQuantities((p) => p.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} disabled={readOnly} />
                            <input className="dl-input" value={q.unit} onChange={(e) => setQuantities((p) => p.map((x, j) => j === i ? { ...x, unit: e.target.value } : x))} disabled={readOnly} placeholder="e.g. tons" />
                            {!readOnly ? (
                              <button className="dl-removebtn" onClick={() => setQuantities((p) => p.filter((_, j) => j !== i))} aria-label="Remove quantity">×</button>
                            ) : <span />}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="dl-muted" style={{ font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)', margin: '0 0 var(--ew-s-3)' }}>No quantities recorded.</p>
                    )}
                    {!readOnly ? (
                      <button className="dl-addbtn" onClick={() => setQuantities((p) => [...p, { item: '', qty: '', unit: '' }])}>+ Add</button>
                    ) : null}
                  </div>
                </div>

                {/* Concrete pours placed today (QA/QC linkage). Checking a
                    confirmed pour + Save or Submit flips it to Poured — the
                    preferred trigger; the QA/QC tab's button is the fallback.
                    Actual CY feeds yield tracking (actual beats the pre-pour
                    estimate in the Materials released-CY roll-up). */}
                {(confirmedPours.length > 0 || flippedPours.length > 0) && (
                  <div className="pw-card">
                    <h2 className="pw-card-hd">Concrete pours placed today</h2>
                    <div className="dl-section">
                      {flippedPours.map((pc) => (
                        <div key={pc.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 0', fontSize: 13 }}>
                          <span>
                            ✓ <b>{pc.element ?? 'Pour'}</b>
                            <span style={{ color: 'var(--ew-n-500)' }}>{[pc.mix, pc.actualQtyCy != null ? `${pc.actualQtyCy} CY actual` : pc.qtyText ? `${pc.qtyText} CY est.` : null].filter(Boolean).map((s) => ` · ${s}`).join('')}</span>
                          </span>
                          <span style={{ color: 'var(--ew-green-700)', fontWeight: 600, whiteSpace: 'nowrap' }}>Poured — recorded on this log</span>
                        </div>
                      ))}
                      {!readOnly && confirmedPours.map((pc) => (
                        <div key={pc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', flexWrap: 'wrap' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', flex: 1, minWidth: 220 }}>
                            <input
                              type="checkbox"
                              checked={pouredIds.includes(pc.id)}
                              onChange={(e) => setPouredIds((prev) => e.target.checked ? [...prev, pc.id] : prev.filter((x) => x !== pc.id))}
                            />
                            <span>
                              <b>{pc.element ?? 'Pour'}</b>
                              <span style={{ color: 'var(--ew-n-500)' }}>{[pc.mix, pc.qtyText ? `${pc.qtyText} CY est.` : null].filter(Boolean).map((s) => ` · ${s}`).join('')}</span>
                            </span>
                          </label>
                          {pouredIds.includes(pc.id) && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ew-n-500)' }}>
                              Actual CY poured
                              <input
                                type="number" min={0} step="0.5"
                                className="dl-input"
                                style={{ width: 90 }}
                                placeholder={pc.qtyCy != null ? String(pc.qtyCy) : ''}
                                value={pourActuals[pc.id] ?? ''}
                                onChange={(e) => setPourActuals((prev) => ({ ...prev, [pc.id]: e.target.value }))}
                              />
                            </label>
                          )}
                        </div>
                      ))}
                      {!readOnly && confirmedPours.length > 0 && (
                        <p className="dl-muted" style={{ font: 'var(--ew-fw-regular) 12px/1.4 var(--ew-font)', marginTop: 4 }}>
                          Checked pours are marked Poured on the QA/QC tab when you save or submit. Actual CY tracks yield against the order.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div className="pw-card">
                  <h2 className="pw-card-hd">Notes</h2>
                  <div className="dl-section">
                    <label className="dl-label">Work summary</label>
                    <textarea className="dl-input" style={{ minHeight: 72, resize: 'vertical' }} value={workSummary} onChange={(e) => setWorkSummary(e.target.value)} disabled={readOnly} />
                  </div>
                  <div className="dl-section">
                    <label className="dl-label">Delays</label>
                    <textarea className="dl-input" style={{ minHeight: 56, resize: 'vertical' }} value={delays} onChange={(e) => setDelays(e.target.value)} disabled={readOnly} />
                  </div>
                  <div className="dl-section">
                    <label className="dl-label">Safety notes</label>
                    <textarea className="dl-input" style={{ minHeight: 56, resize: 'vertical' }} value={safetyNotes} onChange={(e) => setSafetyNotes(e.target.value)} disabled={readOnly} />
                  </div>
                </div>
              </div>

              {/* Right column — actions + equipment on site */}
              <div style={{ display: 'grid', gap: 'var(--ew-s-4)' }}>
                {!readOnly ? (
                  <div className="pw-card">
                    <h2 className="pw-card-hd">Actions</h2>
                    <div className="dl-section">
                      {saveMutation.isError && (
                        <div className="dl-banner-err">{(saveMutation.error as Error).message}</div>
                      )}
                      {submitMutation.isError && (
                        <div className="dl-banner-err">{(submitMutation.error as Error).message}</div>
                      )}
                      <div style={{ display: 'grid', gap: 'var(--ew-s-2)' }}>
                        <Button variant="secondary" onClick={() => saveMutation.mutate()} isLoading={saveMutation.isPending}>
                          Save draft
                        </Button>
                        <Button variant="primary" onClick={() => submitMutation.mutate()} isLoading={submitMutation.isPending}>
                          Submit log
                        </Button>
                      </div>
                      <p className="dl-muted" style={{ font: 'var(--ew-fw-regular) 12px/1.4 var(--ew-font)', marginTop: 'var(--ew-s-2)' }}>
                        Submitting saves your changes, then locks this log for good.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="pw-card">
                    <h2 className="pw-card-hd">Status</h2>
                    <div className="dl-section">
                      <p className="dl-prose" style={{ font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>
                        {d.status === 'submitted'
                          ? `This log was submitted${d.submittedAt ? ` on ${fmtDate(d.submittedAt)}` : ''} and is locked.`
                          /* Say WHY it's read-only rather than showing a "submitted"
                             message about a draft — the two look identical otherwise. */
                          : 'You have view-only access to daily logs. Ask an admin if you need to write them.'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Equipment on site (read-only mirror from Equipment) */}
                <div className="pw-card">
                  <h2 className="pw-card-hd">Equipment on site<span style={{ marginLeft: 'auto', font: 'var(--ew-fw-regular) 11px var(--ew-font)', textTransform: 'none', letterSpacing: 0, color: 'var(--ew-n-400)' }}>from Equipment</span></h2>
                  {equipQuery.isError ? (
                    <div className="dl-section"><p className="dl-muted" style={{ margin: 0, font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>Couldn't load equipment.</p></div>
                  ) : equipQuery.isLoading ? (
                    <div className="dl-section"><p className="dl-muted" style={{ margin: 0, font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>Loading…</p></div>
                  ) : !equipQuery.data || equipQuery.data.length === 0 ? (
                    <div className="dl-section"><p className="dl-muted" style={{ margin: 0, font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>No equipment on this job.</p></div>
                  ) : (
                    equipQuery.data.map((eq) => (
                      <div key={eq.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--ew-s-4)', padding: '10px var(--ew-s-4)', borderBottom: '1px solid var(--ew-n-100)' }}>
                        <span style={{ font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)', color: 'var(--ew-dark)' }}>{eq.equipmentType ?? eq.description ?? eq.title ?? '—'}</span>
                        {eq.status?.label ? <Badge label={eq.status.label} tone={equipTone(eq.status.label)} /> : <span style={{ color: 'var(--ew-n-400)' }}>—</span>}
                      </div>
                    ))
                  )}
                  <div className="dl-section" style={{ borderTop: '1px solid var(--ew-n-100)' }}>
                    <p className="dl-muted" style={{ margin: 0, font: 'var(--ew-fw-regular) 12px/1.4 var(--ew-font)' }}>
                      Pulled live from the Equipment module — manage there.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      </StateView>
    </>
  );
}
